import { inject } from '@angular/core';
import {
  HttpErrorResponse,
  HttpHandlerFn,
  HttpHeaders,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { finalize, switchMap } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '@src/environments/environment';
import { B2bDeviceUtilService } from '@app/services/util-services/root-services/b2b-device-util.service';
import { STORAGE_CONST } from '@app/constants/storage-constants';
import { LocalStorageUtilLibService } from '@b2b/storage-util-lib';
import { Router } from '@angular/router';
import { EventService } from '@app/services/util-services/root-services/event-service';
import { InitAuthService } from '@app/services/global-services/auth/init-auth-service';

let isRefreshing = false;

export const WebNoopHttpInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
) => {
  let newReq: HttpRequest<any>;

  const localStorageUtilLibService: LocalStorageUtilLibService = inject(
    LocalStorageUtilLibService,
  );
  const eventService: EventService = inject(EventService);

  const initAuthService: InitAuthService = inject(InitAuthService);

  const router: Router = inject(Router);

  const b2bDeviceUtilService: B2bDeviceUtilService =
    inject(B2bDeviceUtilService);

  const token: string = localStorageUtilLibService.get(
    STORAGE_CONST.AUTH_TOKEN,
  );
  const fbToken: string = localStorageUtilLibService.get(
    STORAGE_CONST.FIREBASE_AUTH_TOKEN,
  );

  newReq = req.clone({
    headers: getUpdatedHeaders(
      req,
      token,
      fbToken,
      router,
      b2bDeviceUtilService,
    ),
  });

  return next(newReq).pipe(
    finalize(() => {}),
    catchError((error: HttpErrorResponse) => {
      if (error.status === 401 || error.status === 403) {
        if (
          !newReq?.url?.includes('auth-service/new-login') &&
          !newReq?.url?.includes('auth-service/verify-email') &&
          !newReq?.url?.includes('auth-service/generate-login-otp-v2') &&
          !newReq?.url?.includes('auth-service/generate-registration-otp-v2') &&
          !newReq?.url?.includes('auth-service/resend-otp-v2') &&
          !newReq?.url?.includes('auth-service/verify-otp')
        ) {
          if (!isRefreshing) {
            isRefreshing = true;
            const refreshToken: string = localStorageUtilLibService.get(
              STORAGE_CONST.REFRESH_TOKEN,
            );

            if (refreshToken) {
              return createRefreshToken(
                refreshToken,
                token,
                null,
                newReq,
                req,
                next,
                initAuthService,
                router,
                b2bDeviceUtilService,
                eventService,
              );
            }
          }
        }
        initAuthService?.logout();
        throw error;
      } else {
        throw error;
      }
    }),
  );
};

const createRefreshToken = (
  refreshToken: string,
  token: string,
  fbToken: string,
  newReq: HttpRequest<any>,
  req: HttpRequest<any>,
  next: HttpHandlerFn,
  initAuthService: InitAuthService,
  router: Router,
  b2bDeviceUtilService: B2bDeviceUtilService,
  eventService: EventService,
) => {
  return initAuthService.createRefreshToken(refreshToken, token).pipe(
    switchMap(
      (result: {
        token: string;
        refreshToken: string;
        accessToken: string;
      }) => {
        isRefreshing = false;

        newReq = req.clone({
          headers: getUpdatedHeaders(
            req,
            result?.token,
            fbToken,
            router,
            b2bDeviceUtilService,
          ),
        });

        return next(newReq);
      },
    ),
    catchError((err: HttpErrorResponse) => {
      isRefreshing = false;
      /*if (err.status === 401 || err.status === 403) {
        eventService.loggedOutEvent.emit();
        // initAuthService?.logout();
      }*/
      eventService.loggedOutEvent.emit();
      initAuthService.removeRefreshToken().then();
      throw err;
    }),
  );
};

const getUpdatedHeaders = (
  req: HttpRequest<any>,
  at: string,
  ft: string,
  router: Router,
  b2bDeviceUtilService: B2bDeviceUtilService,
): HttpHeaders => {
  let urlPath = router?.routerState?.snapshot?.url;
  if (urlPath && urlPath?.includes('?')) {
    urlPath = urlPath.substring(0, urlPath.indexOf('?'));
  }

  return req.headers
    .append('Authorization', verifyHeaderValue(at))
    .append('x-access-token', verifyHeaderValue(ft))
    .append(
      'x-platform',
      verifyHeaderValue(b2bDeviceUtilService.getWebPlatform()),
    )
    .append('x-os', verifyHeaderValue(b2bDeviceUtilService.getWebOS()))
    .append(
      'x-manufacturer',
      verifyHeaderValue(b2bDeviceUtilService?.getWebVendor()),
    )
    .append('x-app-version', environment?.appVersion)
    .append('x-app-version-code', environment?.appVersionCode)
    .append('x-i-app-version', environment?.iosAppVersion)
    .append('x-i-app-version-code', environment?.iosAppVersionCode)
    .append(
      'x-timezone',
      verifyHeaderValue(Intl.DateTimeFormat().resolvedOptions().timeZone),
    )
    .append('x-device-width', b2bDeviceUtilService.getWidth() + '')
    .append('x-device-height', b2bDeviceUtilService.getHeight() + '')
    .append('x-app-type', 'web')
    .append('x-app-page', urlPath)
    .append('x-req', 'c');
};

const verifyHeaderValue = (value: string) => {
  if (
    !value ||
    value?.trim() === '' ||
    value === 'null' ||
    value === null ||
    value === undefined
  ) {
    return '';
  }
  return value;
};
